import { EPSILON } from 'chart-constants'
import * as d3 from 'd3-selection'
import * as shape from 'd3-shape'
import * as array from 'd3-array'
import { nest } from 'd3-collection'

const labelLineOutLength = 7
    , labelLineInLength = 5
    , pieErosion = 4 // Compensates for the outside of the pie that is taken away with the SVG filter

const midAngle = d => {
  return d.startAngle + (d.endAngle - d.startAngle) / 2
}

const ticks = (selection, innerArc, outerArc) => {
  const labelLineDistance = 6 // distance between the line and the labels

  selection.each(function () {
    let group = d3.select(this)
      , tick = group.selectAll('polyline')
                    .data(function (d) { return [d, d] }) // Two have to draw two polylines

    const points = (d, i) => {
      let start = innerArc.centroid(d) // Start just inside the drawing
        , mid = outerArc.centroid(d) // joint
        , pos = group.datum().position
        , end = [pos[0], pos[1]]
        , offset = end[0] > 0 ? -labelLineDistance : labelLineDistance

      end[0] += offset
      return [start, mid, end]
    }

    tick.exit()
        .remove()

    tick.enter()
        .append('polyline')
        .attr('points', points)
        .merge(tick)
        .attr('points', points)
  })
}

const text = (selection, arc, config) => {
  selection.each(function () {
    let group = d3.select(this)
      , text = group.selectAll('g.text-group')
                    .data(d => [d])
      , _text = text.enter()
                    .append('g')
                    .attr('class', 'text-group')
                    .attr('text-anchor', d => midAngle(d) < Math.PI ? 'start' : 'end')
                    .attr('transform', d => `translate(${d.position})`)
                    .merge(text)

    _text.attr('text-anchor', d => midAngle(d) < Math.PI ? 'start' : 'end')
         .attr('transform', d => `translate(${d.position})`)

    _text.selectAll('text')
         .data(function (d) {
           return [d.data.title, config.value && d.data.subtitle].filter(Boolean)
         })
         .enter()
           .append('text')

    _text.selectAll('text')
         .attr('x', 0)
         .attr('dy', (d, i) => i && `${1.1 * i}em`)
         .text(String)
  })
}

/*
 * Since we spread labels, there's now a chance we have labels outside of the viewport.
 * If that's the case, let's create a virtual stack of labels alloted based on the size.
 * If we have more labels than we can fit, we'll yank labels from the smallest slices.
 */
const checkBounds = (gravity, boundsMultipler, quadrantHeight, labelHeight, slices) => {
  let range = [0, quadrantHeight * boundsMultipler].sort((a, b) => a - b)

  // Check if any of the slices have a y position outside of our range
  let exceeds = slices.find(slice => {
    return slice.position[1] <= range[0] || slice.position[1] > range[1]
  })

  if (exceeds) {
    // We have labels outside, determine a virtual stack based on quadrantHeight, # of labels, and labelHeight
    let maxLabels = Math.floor(quadrantHeight / labelHeight)
      , removeCount = Math.max(0, slices.length - maxLabels)
      , hidden = slices.concat().sort((a, b) => a.value - b.value)
                                .slice(0, removeCount) // Clone the slices and sort so we can determine the smallest slices we need to remove
      , virtualLabelSize = quadrantHeight / (slices.length - removeCount)

    hidden.forEach(slice => delete slice.position) // Hide these labels since we don't have space

    // To make things easy, sort the visible slices so they are always top down
    slices.filter(slice => slice.position)
          .sort((a, b) => a.position[1] - b.position[1])
          .forEach((slice, i) => {
            // then reassign the position from top down
            slice.position[1] = array.min(range) + (i * virtualLabelSize)
          })
  }
}

const spreadClockwise = function (gravity, boundsMultipler, quadrantHeight, labelHeight, slices) {
  for (let i = 0; i < slices.length - 1; i++) {
    if (slices[i].position[1] * gravity - slices[i + 1].position[1] * gravity < labelHeight) {
      slices[i + 1].position[1] = slices[i].position[1] - labelHeight * gravity
    }
  }
  checkBounds.apply(this, arguments)
}

const spreadCounterClockwise = function (gravity, boundsMultipler, quadrantHeight, labelHeight, slices) {
  for (let i = slices.length - 2; i >= 0; i--) {
    if (slices[i].position[1] * gravity - slices[i + 1].position[1] * gravity < labelHeight) {
      slices[i].position[1] = slices[i + 1].position[1] + labelHeight * gravity
    }
  }
  checkBounds.apply(this, arguments)
}

/*
 * Determine which way we spread the labels based on the labels quadrant
 *    2 | 1
 *  ---------
 *    3 | 4
 */
const quadrantSpread = {
  1: spreadCounterClockwise.bind(null, -1, -1),
  2: spreadClockwise.bind(null, 1, -1),
  3: spreadCounterClockwise.bind(null, 1, 1),
  4: spreadClockwise.bind(null, -1, 1)
}

const labels = (selection, styles, config) => {
  selection.each(function (d) {
    let container = d3.select(this)
      , total = d.slices.reduce((p, c) => {
        return (c.data.raw || 0) + p
      }, 0)
      , labelHeight = d.slices.find(d => d.data.subtitle) ? 28 : 14 // Double the size if we are showing subtitles
      , outerArcRadius = styles.outerRadius + labelLineOutLength
      , innerArcRadius = styles.outerRadius - (styles.innerRadius ? labelLineInLength : labelLineInLength + pieErosion)
      , outerArc = shape.arc()
                        .outerRadius(outerArcRadius)
                        .innerRadius(outerArcRadius)
      , innerArc = shape.arc()
                        .outerRadius(innerArcRadius)
                        .innerRadius(innerArcRadius)
      , quadrants = nest().key(d => d.quadrant)
                          .entries(d.slices.map(slice => {
                            let position = outerArc.centroid(slice)
                              , align = midAngle(slice) < Math.PI ? 'start' : 'end'

                            // Force vertical alignments so we have a stack on the right and a stack on the left
                            position[0] = outerArc.innerRadius()() * (midAngle(slice) < Math.PI ? 1 : -1)
                            slice.position = position
                            slice.align = align

                            if (position[1] < 0) {
                              // top half of ring
                              slice.quadrant = align === 'start' ? 1 : 2
                            } else {
                              // bottom half
                              slice.quadrant = align === 'start' ? 4 : 3
                            }
                            return slice
                          }))
    // Now that we have the labels separated into their quadrants, let's recalculate their positions if they are too tight or overlapping
    quadrants.forEach(quadrant => {
      var quadrantHeight = d.dimensions.height / 2
      if (quadrant.key == 3 || quadrant.key == 4) {
        // Make sure the bottom quadrant bottom labels don't exceed the lower bounds when we include their height
        quadrant.values.forEach(slice => {
          var delta = slice.position[1] + labelHeight - quadrantHeight
          if (delta > 0) {
            slice.position[1] -= delta
          }
        })
      }
      quadrantSpread[quadrant.key](quadrantHeight, labelHeight, quadrant.values)
    })

    // Merge the quadrants into a flat array of labels
    let labelData = [].concat(...quadrants.map(q => q.values))
      , label = container.selectAll('g.label')
                         .data(labelData.filter(slice => {
                           if (total === 0) {
                             // The pie total is 0, meaning we have a bunch of slices of 0 values. We can't draw the labels
                             return null
                           }
                           return slice.position &&
                            (slice.data.title || (config.value && slice.data.subtitle)) &&
                            slice.data.raw // Also hide labels if we don't have a raw data value
                         }), d => d.data.serieId)
    label.exit().remove()

    label.enter()
         .append('g')
           .attr('class', 'label')
           .merge(label)
           .call(text, outerArc, config)
           .call(ticks, innerArc, outerArc)
  })
}

/*
 * Draws slice labels for the pies
 */
export default (selection, pies, styles, chart) => {
  selection.each(function (d) {
    let container = d3.select(this)
                      .selectAll('g.pie-slice-labels-container').data([pies])
      , enter = container.enter()
                         .append('g')
                           .attr('class', 'pie-slice-labels-container')

    container = container.merge(enter)
                         .style('opacity', EPSILON)

    // One group for each pie
    let group = container.selectAll('g.pie-slice-labels')
                         .data(d => d)

    group.exit().remove()

    group.enter()
         .append('g')
           .attr('class', 'pie-slice-labels')
           .merge(group)
           .attr('transform', d => {
             let dimensions = d.dimensions
             return `translate(${dimensions.x + dimensions.width / 2}, ${dimensions.y + dimensions.height / 2})`
           })
           .call(labels, styles, chart.datum().axes.pie)

    chart.defaultTransition.end().then(() => {
      container.style('opacity', 1)
    }).catch(() => {
      // interrupted transition, ignore
    })
  })
}
